import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ListChecks, Star, Trophy, Gift, ShieldAlert, Clock, XCircle, Calendar } from "lucide-react";
import { api, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function DashboardHelper() {
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!(user.helper_profile || {}).approved) return;
    api.get("/tasks?status=published").then((r) => setAvailable(r.data.filter((t) => !t.helper_id))).catch(() => {});
    api.get("/tasks?mine=helper").then((r) => setMine(r.data)).catch(() => {});
  }, [user]);

  const profile = user.helper_profile || {};
  const approved = profile.approved;
  const appStatus = profile.application_status || "not_submitted";
  const active = mine.filter((t) => !["paid_out", "cancelled"].includes(t.status));

  return (
    <div className="space-y-8">
      {!approved && (
        <div className="bg-lime/10 border-2 border-lime rounded-2xl p-6 flex items-start gap-4" data-testid="application-banner">
          <div className="h-12 w-12 rounded-full bg-lime text-navy flex items-center justify-center shrink-0">
            {appStatus === "pending" ? <Clock size={22} /> : appStatus === "rejected" ? <XCircle size={22} /> : <ShieldAlert size={22} />}
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-black text-xl text-navy">
              {appStatus === "not_submitted" && "Complète ta candidature"}
              {appStatus === "pending" && "Candidature en cours d'examen"}
              {appStatus === "rejected" && "Candidature refusée"}
              {appStatus === "approved" && "Approuvé !"}
            </h2>
            <p className="text-[#475569] mt-1">
              {appStatus === "not_submitted" && "Remplis le formulaire officiel pour commencer à accepter des tâches."}
              {appStatus === "pending" && "L'équipe Go Helper examine ton dossier. Réponse sous 24-48h."}
              {appStatus === "rejected" && "Tu peux modifier et resoumettre ta candidature."}
            </p>
            <Link to="/helper/apply" className="btn-primary mt-3 inline-flex" data-testid="apply-link">
              {appStatus === "not_submitted" ? "Remplir maintenant" : "Voir ma candidature"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}

      <section className="bg-gradient-to-br from-navy to-[#0F2342] text-white rounded-3xl p-6 md:p-10 relative overflow-hidden noise">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative">
          <span className="chip-lime">Helper</span>
          <h1 className="text-3xl md:text-4xl font-heading font-black mt-3 text-white">Prêt à aider, {user.full_name.split(" ")[0]} ?</h1>
          <p className="text-white/80 mt-1">
            {approved ? `${available.length} tâche(s) disponible(s) dans ton coin.` : "Tes tâches apparaîtront ici après l'approbation."}
          </p>

          <div className="grid grid-cols-3 gap-3 mt-6 max-w-2xl">
            <Stat icon={<Trophy size={16} />} label="Statut" value={profile.badge || "Helper"} />
            <Stat icon={<ListChecks size={16} />} label="Tâches" value={profile.tasks_completed || 0} />
            <Stat icon={<Star size={16} />} label="Note" value={(profile.rating || 0).toFixed(1)} />
          </div>
        </div>
      </section>

      {profile.bonus_earned > 0 && (
        <div className="card-surface p-4 flex items-center gap-3" data-testid="bonus-banner">
          <div className="h-10 w-10 rounded-full bg-lime flex items-center justify-center"><Gift size={18} className="text-navy" /></div>
          <div>
            <div className="font-bold text-navy">Bonus gagné</div>
            <div className="text-sm text-[#475569]">Tu as accumulé {profile.bonus_earned.toFixed(2)} $ de bonus parrainage.</div>
          </div>
          <Link to="/referral" className="ml-auto btn-outline py-2 px-4 text-sm">Voir</Link>
        </div>
      )}

      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="text-2xl font-heading font-black text-navy">Tâches disponibles</h2>
          <Link to="/tasks" className="text-navy font-bold hover:underline text-sm" data-testid="see-all-tasks">Tout voir →</Link>
        </div>
        {available.length === 0 ? (
          <div className="card-surface p-8 text-center text-[#475569]">Aucune tâche disponible pour le moment. Reviens bientôt !</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {available.slice(0, 4).map((t) => <HelperTaskCard key={t.id} task={t} />)}
          </div>
        )}
      </section>

      {active.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-2xl font-heading font-black text-navy">Mes tâches en cours</h2>
            <Link to="/calendar" className="text-sm font-bold text-navy hover:text-lime inline-flex items-center gap-1" data-testid="helper-cal-link">
              <Calendar size={14} /> Calendrier
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {active.map((t) => <HelperTaskCard key={t.id} task={t} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white/10 rounded-xl p-3 backdrop-blur">
      <div className="text-white/70 text-xs flex items-center gap-1 font-bold uppercase tracking-wide">{icon} {label}</div>
      <div className="font-heading font-black text-white text-lg mt-1">{value}</div>
    </div>
  );
}

function HelperTaskCard({ task }) {
  const s = STATUS_LABELS[task.status];
  return (
    <Link to={`/tasks/${task.id}`} className="card-interactive p-5 block" data-testid={`helper-task-${task.id}`}>
      <div className="flex items-center justify-between">
        <span className="chip">{task.category.replace(/_/g, " ")}</span>
        {task.urgent && <span className="chip-lime">Urgent</span>}
      </div>
      <h3 className="font-heading font-black text-navy text-xl mt-3 line-clamp-1">{task.title}</h3>
      <p className="text-sm text-[#475569] mt-1">{task.area}</p>
      <div className="flex items-end justify-between mt-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Tu recevras</div>
          <div className="font-heading font-black text-3xl text-navy">{task.helper_subtotal.toFixed(2)} $</div>
        </div>
        <span className={`chip ${s.color}`}>{s.fr}</span>
      </div>
    </Link>
  );
}
